import React, { useState, useRef } from 'react';
import { uploadAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Upload, X, Image as ImageIcon, Link as LinkIcon, Loader2 } from 'lucide-react';

const BACKEND_URL = '';

export default function ImageUpload({ 
  value, 
  onChange, 
  imageType = 'general',
  label = 'Image',
  showUrlInput = true,
  compact = false,
  className = '',
  previewClassName = 'h-32 w-auto'
}) {
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState(value?.startsWith('http') || value?.startsWith('/api') ? 'preview' : 'upload');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 2MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Use JPG, PNG, or WebP');
      return;
    }

    // Check image dimensions
    const img = new window.Image();
    img.src = URL.createObjectURL(file);
    
    img.onload = async () => {
      URL.revokeObjectURL(img.src);
      
      if (img.width > 2000) {
        toast.info('Image will be resized to max 2000px width');
      }

      setUploading(true);
      try {
        const response = await uploadAPI.uploadImage(file, imageType);
        const imageUrl = response.data.image_url;
        onChange(imageUrl);
        setMode('preview');
        toast.success('Image uploaded successfully');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to upload image');
      } finally {
        setUploading(false);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      toast.error('Invalid image file');
    };
  };

  const handleUrlChange = (url) => {
    onChange(url);
    if (url) setMode('preview');
  };

  const handleClear = () => {
    onChange('');
    setMode('upload');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api')) return `${BACKEND_URL}${url}`;
    return url;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <Label>{label}</Label>}
      
      {/* Preview */}
      {value && mode === 'preview' && !compact && (
        <div className="relative inline-block">
          <img 
            src={getFullUrl(value)} 
            alt="Preview" 
            className={`rounded-lg object-cover border ${previewClassName}`}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="destructive"
            className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
            onClick={handleClear}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Compact Mode - Just a button */}
      {compact && (
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                {value ? 'Change Image' : 'Upload Image'}
              </>
            )}
          </Button>
        </div>
      )}

      {/* Full Upload Area (non-compact) */}
      {!compact && (!value || mode !== 'preview') && (
        <div className="space-y-3">
          {/* File Upload */}
          <div 
            className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="text-sm">Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <Upload className="h-8 w-8" />
                <span className="text-sm">Click to upload image</span>
                <span className="text-xs">JPG, PNG, WebP • Max 2MB • Max 2000px width</span>
              </div>
            )}
          </div>

          {/* URL Input (optional) */}
          {showUrlInput && (
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="url"
                  placeholder="Or paste image URL..."
                  value={value || ''}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}